import type { ReactNode } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { ProtectedRoute } from '../components/auth/index.js';
import { LoadState } from '../components/features/org-issues/index.js';
import {
  AutoBlocksPanel,
  CarriedActions,
  MeetingBoard,
} from '../components/features/team-meetings/index.js';
import { useAuth } from '../hooks/useAuth.js';
import { useOrgBoards } from '../hooks/useOrgIssues.js';
import {
  useTeamMeeting,
  useTeamMeetingAutoBlocks,
  useTeamMeetingSeries,
} from '../hooks/useTeamMeetings.js';
import { meetingOptions, parseMeetingSearch, viewerIdOf } from '../lib/team-meetings.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の開催回。上部に定例の自動ブロック（KPI・休日オペ担・案件）と持ち越しの Next、下にまだ棚卸ししていない投稿（全部）を区分ごとの列で並べ、リーダー以上がネクストアクション（チーム課題・改善要望・対応を書いてクローズ）を決めて棚卸しする。この回で棚卸しした投稿も並べ、開催回を切り替えられる。開いている開催回は URL に残す
 * @graph-connects useTeamMeetingSeries [calls] シリーズと開催回の一覧
 * @graph-connects useTeamMeeting [calls] まだ棚卸ししていない投稿・この回で棚卸しした投稿・持ち越しの Next
 * @graph-connects useTeamMeetingAutoBlocks [calls] 自動ブロック
 * @graph-connects MeetingBoard [calls] 区分ごとの投稿と棚卸し
 * @graph-connects AutoBlocksPanel [calls] 自動ブロック
 * @graph-connects CarriedActions [calls] 持ち越しの Next
 */
export const Route = createFileRoute('/team-meetings/$seriesId')({
  component: TeamMeetingRoute,
  validateSearch: parseMeetingSearch,
});

/** @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする */
function TeamMeetingRoute(): ReactNode {
  return (
    <ProtectedRoute pageKey="teamMeetings">
      <TeamMeetingPage />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function TeamMeetingPage(): ReactNode {
  const { t } = useTranslation();
  const { seriesId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const auth = useAuth();
  const detail = useTeamMeetingSeries(seriesId);
  const boards = useOrgBoards();
  const meetingId = search.meeting ?? detail.data?.currentMeetingOn ?? null;
  const meeting = useTeamMeeting(seriesId, meetingId);
  const blocks = useTeamMeetingAutoBlocks(seriesId, meetingId);

  if (detail.data === undefined) {
    return (
      <LoadState
        isLoading={detail.isLoading}
        isError={detail.isError}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }
  const { series, permissions } = detail.data;
  const options = meetingOptions(detail.data);
  const defaultBoard =
    boards.data?.boards.find(({ board }) => board.id === series.defaultBoardId)?.board ?? null;
  const people = { ...detail.data.people, ...meeting.data?.people, ...blocks.data?.people };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/team-meetings"
            className="text-xs text-[var(--color-text-muted)] hover:opacity-80"
          >
            {t('teamMeetings.back')}
          </Link>
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">{series.name}</h1>
        </div>
        <Select
          label={t('teamMeetings.meeting.select')}
          value={meetingId ?? ''}
          options={options.map((o) => ({
            value: o.meetingId,
            label: o.isUpcoming
              ? t('teamMeetings.meeting.upcoming', { date: o.meetingId })
              : o.meetingId,
          }))}
          onChange={(e) => {
            void navigate({ search: { meeting: e.target.value } });
          }}
        />
      </div>

      {meetingId !== null && (
        <>
          <AutoBlocksPanel
            blocks={blocks.data?.blocks}
            isLoading={blocks.isLoading}
            isError={blocks.isError}
            people={people}
          />
          <LoadState
            isLoading={meeting.isLoading}
            isError={meeting.isError}
            onRetry={() => {
              void meeting.refetch();
            }}
          />
          {meeting.isError && (
            <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]">
              {meeting.error.message}
            </p>
          )}
          {meeting.data !== undefined && (
            <>
              <CarriedActions
                boardId={series.defaultBoardId}
                actions={meeting.data.carriedActions}
                people={people}
              />
              <MeetingBoard
                series={series}
                meetingId={meeting.data.meetingId}
                openPosts={meeting.data.openPosts}
                triagedPosts={meeting.data.triagedPosts}
                people={people}
                viewerId={viewerIdOf(people, auth.user?.email)}
                canPost={permissions.canPost}
                defaultBoard={defaultBoard}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
